/**
 * Autenticação no navegador (mesmo desenho do Precificador):
 * - conta Microsoft (Entra ID): MSAL com código + PKCE; o token de acesso da API vai como Bearer;
 * - e-mail e senha (emergência): sessão `lcl_…` no sessionStorage (some ao fechar a aba);
 * - desenvolvimento: escolha de um usuário de teste (cabeçalho x-dev-user), só com a API em modo dev.
 * As formas disponíveis vêm do servidor (/api/v1/auth/config).
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { InteractionRequiredAuthError, PublicClientApplication, type AuthenticationResult } from '@azure/msal-browser';
import { ApiError, api, configureApi } from './api';
import { squarePhotoBase64 } from './files';
import type { Me } from './types';
import { appPath, appReplace, isAuthResponse } from './router';

export let AUTH_MODE: 'dev' | 'entra' | 'local' = 'local';
export const AUTH_METHODS = { password: true, entra: false };
const TOKEN_KEY = 'pmis.session';
const DEV_KEY = 'pmis.devUser';
const RESUME_KEY = 'pmis.resume';
const REDIRECT_AT_KEY = 'pmis.entraRedirectAt';
const GRAPH_SCOPES = ['User.Read'];

let msal: PublicClientApplication | null = null;
let API_SCOPES: string[] = [];

const store = {
  get: (k: string) => { try { return sessionStorage.getItem(k); } catch { return null; } },
  set: (k: string, v: string | null) => { try { if (v) sessionStorage.setItem(k, v); else sessionStorage.removeItem(k); } catch { /* navegação privada */ } },
};
let sessionToken: string | null = store.get(TOKEN_KEY);
let devUser: string | null = store.get(DEV_KEY);

// A Microsoft devolve o login no fim do endereço (#code=…). O roteador deixa a barra só com o domínio assim que a primeira
// tela aparece, então a resposta é guardada aqui, antes de qualquer tela, e entregue ao MSAL. Sem isso, a resposta se
// perdia, o PMIS pedia o login de novo e a página ficava recarregando (0.6.5 a 0.7.12).
const REDIRECT_HASH = isAuthResponse(window.location.hash) ? window.location.hash : '';

/** O PMIS mandou para a Microsoft há menos de 1 minuto e voltou sem login: não manda de novo sozinho (evita o vaivém). */
class EntraRetryError extends Error {}

async function loadAuthConfig() {
  const r = await fetch('/api/v1/auth/config', { headers: { Accept: 'application/json' } });
  if (!r.ok) throw new Error('Servidor indisponível. Tente de novo em instantes.');
  const c = (await r.json()) as { mode: typeof AUTH_MODE; password: boolean; entra: { tenantId: string; clientId: string; scope: string } | null };
  AUTH_MODE = c.mode;
  AUTH_METHODS.password = c.password;
  AUTH_METHODS.entra = !!c.entra;
  msal = null;
  if (c.entra) {
    API_SCOPES = [c.entra.scope];
    msal = new PublicClientApplication({
      // A tela de antes do login volta pelo RESUME_KEY (o endereço mostra só o domínio): o MSAL não precisa navegar de novo.
      auth: { clientId: c.entra.clientId, authority: `https://login.microsoftonline.com/${c.entra.tenantId}`, redirectUri: window.location.origin, navigateToLoginRequestUrl: false },
      cache: { cacheLocation: 'sessionStorage' },
    });
  }
}

async function entraToken(): Promise<string | null> {
  if (!msal) return null;
  const account = msal.getActiveAccount() ?? msal.getAllAccounts()[0];
  if (!account) return null;
  try {
    return (await msal.acquireTokenSilent({ scopes: API_SCOPES, account })).accessToken;
  } catch (e) {
    if (e instanceof InteractionRequiredAuthError) {
      const last = Number(store.get(REDIRECT_AT_KEY) ?? 0);
      if (Date.now() - last < 60_000) throw new EntraRetryError('A Microsoft não confirmou a entrada. Clique em "Entrar com a conta Microsoft".');
      store.set(REDIRECT_AT_KEY, String(Date.now()));
      store.set(RESUME_KEY, appPath());
      await msal.acquireTokenRedirect({ scopes: API_SCOPES, account });
    }
    throw e;
  }
}

/** Foto da conta Microsoft (Graph /me/photo), com a permissão básica User.Read; null se a conta não tem foto. */
export async function entraPhoto(interactive: boolean): Promise<Blob | null> {
  const account = msal?.getActiveAccount() ?? msal?.getAllAccounts()[0];
  if (!msal || !account) throw new Error('Entre com a conta Microsoft para trazer a foto dela.');
  let token: string;
  try {
    token = (await msal.acquireTokenSilent({ scopes: GRAPH_SCOPES, account })).accessToken;
  } catch (e) {
    if (!interactive || !(e instanceof InteractionRequiredAuthError)) throw e;
    token = (await msal.acquireTokenPopup({ scopes: GRAPH_SCOPES, account })).accessToken;
  }
  for (const path of ['/me/photos/240x240/$value', '/me/photo/$value']) {
    const r = await fetch(`https://graph.microsoft.com/v1.0${path}`, { headers: { Authorization: `Bearer ${token}` } });
    if (r.ok) return r.blob();
    if (r.status !== 404) throw new Error(`A Microsoft não devolveu a foto agora (erro ${r.status}).`);
  }
  return null;
}

const CALENDAR_SCOPES = ['Calendars.ReadBasic'];
const CALENDAR_RESUME = 'pmis.calendarResume';
const CALENDAR_ERROR = 'pmis.calendarError';
const CALENDAR_AT = 'pmis.calendarRedirectAt';
const CALENDAR_DENIED = 'A Microsoft não liberou a leitura da agenda. Peça ao Administrador do Microsoft 365 a permissão Calendars.ReadBasic para o PMIS (Manual › Integração: login pela conta Microsoft).';

/**
 * Token do Graph para ler a agenda da própria pessoa (Calendars.ReadBasic: assunto, horário e local, sem o conteúdo
 * dos convites). Na primeira vez, a Microsoft pede o consentimento: a página vai até ela e volta para o apontamento,
 * que reabre a importação sozinha.
 */
export async function calendarToken(): Promise<string> {
  const account = msal?.getActiveAccount() ?? msal?.getAllAccounts()[0];
  if (!msal || !account) throw new Error('Para trazer a agenda do Outlook, entre no PMIS com a conta Microsoft.');
  try {
    return (await msal.acquireTokenSilent({ scopes: CALENDAR_SCOPES, account })).accessToken;
  } catch (e) {
    if (!(e instanceof InteractionRequiredAuthError)) throw e;
    // Já foi à Microsoft há pouco e voltou sem a permissão: não vai de novo sozinho (evita o vaivém).
    if (Date.now() - Number(store.get(CALENDAR_AT) ?? 0) < 120_000) throw new Error(CALENDAR_DENIED);
    store.set(CALENDAR_AT, String(Date.now()));
    store.set(RESUME_KEY, appPath());
    store.set(CALENDAR_RESUME, '1');
    await msal.acquireTokenRedirect({ scopes: CALENDAR_SCOPES, account });
    throw new Error('Indo à Microsoft para liberar a leitura da agenda…');
  }
}
/** Voltou da Microsoft depois de liberar a agenda? (Uma vez só.) Com erro, devolve a mensagem para a tela. */
export function calendarResume(): { reopen: boolean; error: string | null } {
  const reopen = store.get(CALENDAR_RESUME) === '1';
  const error = store.get(CALENDAR_ERROR);
  store.set(CALENDAR_RESUME, null);
  store.set(CALENDAR_ERROR, null);
  return { reopen: reopen && !error, error };
}

export const entraAvailable = () => !!msal && !!(msal.getActiveAccount() ?? msal.getAllAccounts()[0]);

export async function saveEntraPhoto(interactive: boolean): Promise<boolean> {
  const blob = await entraPhoto(interactive);
  if (!blob) return false;
  await api('/api/v1/me/photo', { method: 'PUT', body: { dataBase64: await squarePhotoBase64(blob), source: 'entra' } });
  return true;
}

type State =
  | { status: 'loading' }
  | { status: 'anonymous'; message?: string }
  | { status: 'must-change' }
  | { status: 'no-access'; message: string }
  | { status: 'error'; message: string }
  | { status: 'ready'; me: Me };

interface AuthApi {
  state: State;
  me: Me | null;
  loginPassword: (email: string, password: string) => Promise<void>;
  loginEntra: () => Promise<void>;
  loginDev: (email: string) => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  reloadMe: () => Promise<void>;
  logout: () => void;
}

const Ctx = createContext<AuthApi | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<State>({ status: 'loading' });

  const signedOut = useCallback((message?: string) => {
    sessionToken = null;
    devUser = null;
    store.set(TOKEN_KEY, null);
    store.set(DEV_KEY, null);
    setState({ status: 'anonymous', message });
  }, []);

  const loadMe = useCallback(async () => {
    try {
      const me = await api<Me>('/api/v1/me');
      setState({ status: 'ready', me });
      if (me.authMethod === 'entra' && !me.photoVersion && entraAvailable()) {
        const tried = store.get('pmis.photoTried') === '1';
        store.set('pmis.photoTried', '1');
        if (!tried && await saveEntraPhoto(false).catch(() => false)) setState({ status: 'ready', me: await api<Me>('/api/v1/me') });
      }
    } catch (e) {
      if (e instanceof ApiError && e.status === 403 && e.problem.code === 'PASSWORD_CHANGE_REQUIRED') setState({ status: 'must-change' });
      else if (e instanceof ApiError && e.status === 403) setState({ status: 'no-access', message: e.message });
      else if (e instanceof ApiError && e.status === 401) signedOut(e.problem.code === 'REAUTH_REQUIRED' || e.problem.code === 'LOGIN_METHOD_OFF' ? e.message : undefined);
      else if (e instanceof EntraRetryError) signedOut(e.message);
      else setState({ status: 'error', message: e instanceof Error ? e.message : String(e) });
    }
  }, [signedOut]);

  useEffect(() => {
    configureApi({
      headers: async (): Promise<Record<string, string>> => {
        if (sessionToken) return { Authorization: `Bearer ${sessionToken}` };
        if (devUser && AUTH_MODE === 'dev') return { 'x-dev-user': devUser };
        const t = await entraToken();
        return t ? { Authorization: `Bearer ${t}` } : {};
      },
      onUnauthorized: (p) => { if (sessionToken || devUser || p?.code) signedOut(p?.detail ?? 'Sua sessão terminou. Entre de novo.'); },
    });
    (async () => {
      try { await loadAuthConfig(); } catch (e) { setState({ status: 'error', message: e instanceof Error ? e.message : String(e) }); return; }
      if (sessionToken || (devUser && AUTH_MODE === 'dev')) return loadMe();
      if (msal) {
        await msal.initialize();
        let r: AuthenticationResult | null = null;
        let failure = '';
        try { r = await msal.handleRedirectPromise(REDIRECT_HASH || undefined); } catch (e) { failure = e instanceof Error ? e.message : String(e); }
        if (REDIRECT_HASH) {
          // O MSAL limpa o endereço depois de ler a resposta (e, com ele, a tela guardada no histórico): volta a tela.
          const resume = store.get(RESUME_KEY);
          store.set(RESUME_KEY, null);
          appReplace(resume || appPath());
        }
        if (r?.account) {
          store.set(REDIRECT_AT_KEY, null);
          msal.setActiveAccount(r.account);
          await api('/api/v1/auth/entra/login', { method: 'POST' }).catch(() => undefined);
        } else if (failure) {
          // Recusa ao liberar a agenda: o login continua valendo, e o apontamento mostra o aviso.
          if (store.get(CALENDAR_RESUME) === '1' && (msal.getActiveAccount() ?? msal.getAllAccounts()[0])) store.set(CALENDAR_ERROR, `${CALENDAR_DENIED} (${failure.slice(0, 200)})`);
          else {
            setState({ status: 'anonymous', message: `A Microsoft não completou a entrada: ${failure}` });
            return;
          }
        }
        const acc = msal.getActiveAccount() ?? msal.getAllAccounts()[0];
        if (acc) { msal.setActiveAccount(acc); return loadMe(); }
      }
      setState({ status: 'anonymous' });
    })();
  }, [loadMe, signedOut]);

  const value = useMemo<AuthApi>(() => ({
    state,
    me: state.status === 'ready' ? state.me : null,
    loginPassword: async (email, password) => {
      const r = await api<{ token: string; mustChangePassword: boolean }>('/api/v1/auth/login', { method: 'POST', body: { email, password } });
      sessionToken = r.token;
      store.set(TOKEN_KEY, r.token);
      if (r.mustChangePassword) setState({ status: 'must-change' });
      else await loadMe();
    },
    loginEntra: async () => {
      await msal!.initialize();
      store.set(RESUME_KEY, appPath());
      await msal!.loginRedirect({ scopes: API_SCOPES });
    },
    loginDev: async (email) => {
      devUser = email;
      store.set(DEV_KEY, email);
      await loadMe();
    },
    changePassword: async (currentPassword, newPassword) => {
      await api('/api/v1/auth/password', { method: 'POST', body: { currentPassword, newPassword } });
      await loadMe();
    },
    reloadMe: loadMe,
    logout: () => {
      if (sessionToken) void api('/api/v1/auth/logout', { method: 'POST' }).catch(() => undefined).finally(() => signedOut());
      else if (msal?.getActiveAccount()) void msal.logoutRedirect();
      else signedOut();
    },
  }), [state, loadMe, signedOut]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthApi {
  const v = useContext(Ctx);
  if (!v) throw new Error('AuthProvider ausente');
  return v;
}

/** A pessoa tem a permissão (em qualquer alcance)? A API é quem decide de fato. */
export function useCan(code: string): boolean {
  const { me } = useAuth();
  return !!me?.permissions[code];
}
